import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'reactflow/dist/style.css';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Node,
  Edge,
  Connection,
  OnConnect
} from 'reactflow';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '@os/lib/utils';
import { supabase } from '@os/integrations/supabase/client';
import { useAuth } from '@os/hooks/useAuth';
import NodePalette from './blueprint/NodePalette';
import { nodeTypes, getNodeDef } from './blueprint/BlueprintNode';
import { executeBlueprint, ExecutionContext } from '@/lib/blueprintExecutor';
import { initialNodes as defaultInitialNodes, initialEdges as defaultInitialEdges } from '@/lib/blueprintDefaults';
import { useKnowledgeBaseCore } from '@/hooks/useKnowledgeBaseCore';
import { useMCPServices } from '@/hooks/supabase/useMCPServices';
import { useSupabaseStore } from '@/store/SupabaseStore';
import ModelSelector from '@/components/chat/ModelSelector';
import { Play, Save, Cpu, ChevronLeft, Share2, Lock, Globe, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function hasPathFromStartToEnd(nodes: Node[], edges: Edge[]): boolean {
  const startNode = nodes.find(n => n.type === 'start');
  const endNode = nodes.find(n => n.type === 'end');
  if (!startNode || !endNode) return false;

  const adj: Record<string, string[]> = {};
  nodes.forEach(n => { adj[n.id] = []; });
  edges.forEach(e => { if (adj[e.source]) adj[e.source].push(e.target); });

  const visited = new Set<string>();
  const queue = [startNode.id];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    if (curr === endNode.id) return true;
    if (!visited.has(curr)) {
      visited.add(curr);
      for (const neighbor of adj[curr] || []) {
        if (!visited.has(neighbor)) queue.push(neighbor);
      }
    }
  }
  return false;
}

const LOCAL_KEY = 'mapleos_blueprint_studio';

export default function BlueprintStudio() {
  const { user } = useAuth();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesState] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const selectedNode = useMemo(() => nodes.find(n => n.id === selectedNodeId) || null, [selectedNodeId, nodes]);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<ExecutionContext | null>(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runInitialInput, setRunInitialInput] = useState('');

  const [blueprintName, setBlueprintName] = useState('Untitled');
  const [blueprintDesc, setBlueprintDesc] = useState<string | null>('Description of your blueprint...');
  const [isPublic, setIsPublic] = useState(false);

  const models = useSupabaseStore(state => state.models);
  const { knowledgeBases, fetchKnowledgeBases } = useKnowledgeBaseCore();
  const { services: mcpServices, userApiKeys } = useMCPServices();

  useEffect(() => {
    setIsPanelOpen(!!selectedNodeId);
  }, [selectedNodeId]);

  useEffect(() => {
    // Initialize with default nodes/edges or load local
    const local = localStorage.getItem(LOCAL_KEY);
    if (local) {
      try {
        const parsed = JSON.parse(local);
        setNodes(parsed.nodes || defaultInitialNodes);
        setEdges(parsed.edges || defaultInitialEdges);
        setBlueprintName(parsed.name || 'Untitled');
        setBlueprintDesc(parsed.description || null);
        setIsPublic(!!parsed.public);
      } catch {
        setNodes(defaultInitialNodes);
        setEdges(defaultInitialEdges);
      }
    } else {
      setNodes(defaultInitialNodes);
      setEdges(defaultInitialEdges);
    }
    fetchKnowledgeBases();
  }, [setNodes, setEdges, fetchKnowledgeBases]);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const type = event.dataTransfer.getData('application/reactflow');
    if (!type || !reactFlowInstance) return;
    const position = reactFlowInstance.project({
      x: event.clientX - (reactFlowWrapper.current?.getBoundingClientRect().left || 0),
      y: event.clientY - (reactFlowWrapper.current?.getBoundingClientRect().top || 0)
    });
    const def = getNodeDef(type);
    setNodes(nds => nds.concat({
      id: `${type}-${+new Date()}`,
      type,
      position,
      data: { type, label: def?.title || type, description: def?.subtitle || '', content: '' }
    }));
  }, [reactFlowInstance, setNodes]);

  const onConnect: OnConnect = useCallback((params: Edge | Connection) => {
    setEdges(eds => addEdge({ ...params, type: 'smoothstep', animated: true, style: { strokeWidth: 2 } }, eds));
  }, [setEdges]);

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);
  const onPaneClick = useCallback(() => setSelectedNodeId(null), []);

  const handleNodeDataChange = (field: string, value: any) => {
    if (!selectedNodeId) return;
    setNodes(nds => nds.map(node => node.id === selectedNodeId ? { ...node, data: { ...node.data, [field]: value } } : node));
  };

  const saveLocal = () => {
    const payload = {
      name: blueprintName,
      description: blueprintDesc,
      public: isPublic,
      nodes,
      edges
    };
    localStorage.setItem(LOCAL_KEY, JSON.stringify(payload));
  };

  const saveToSupabase = async () => {
    if (!user) {
      saveLocal();
      return;
    }

    const sb: any = supabase;
    const isUniqueViolation = (err: any) => {
      return err?.code === '23505' || String(err?.message || '').includes('unique_user_blueprint_name');
    };

    for (let i = 0; i < 20; i++) {
      const name = i === 0 ? blueprintName : `${blueprintName} (${i + 1})`;
      const { data, error } = await sb
        .from('blueprints')
        .insert({
          user_id: user.id,
          name,
          description: blueprintDesc,
          is_private: !isPublic,
          data: { nodes, edges }
        })
        .select('id')
        .single();

      if (!error) {
        // store reference
        localStorage.setItem(`${LOCAL_KEY}_last_id`, data.id);
        return;
      }

      if (!isUniqueViolation(error)) {
        // fallback to local
        saveLocal();
        return;
      }
    }

    // fallback to local after retries
    saveLocal();
  };

  const runBlueprint = () => {
    if (!hasPathFromStartToEnd(nodes, edges)) {
      setRunDialogOpen(true);
      setExecutionResult({ variables: {}, history: [], error: 'Please connect Start → End before running.' });
      return;
    }
    setRunInitialInput(localStorage.getItem(`${LOCAL_KEY}_input`) || '');
    setExecutionResult(null);
    setRunDialogOpen(true);
  };

  const executeFlow = async () => {
    if (!runInitialInput.trim()) return;
    localStorage.setItem(`${LOCAL_KEY}_input`, runInitialInput);
    setIsExecuting(true);
    setExecutionResult(null);
    try {
      const result = await executeBlueprint(nodes, edges, runInitialInput, models, mcpServices, userApiKeys);
      setExecutionResult(result);
    } catch (error: any) {
      setExecutionResult({ variables: {}, history: [], error: error.message || 'An unknown error occurred.' });
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-background to-muted/40">
      <header className="flex items-center justify-between p-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" title="Back">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Input
            value={blueprintName}
            onChange={e => setBlueprintName(e.target.value)}
            className="text-lg font-semibold w-64"
          />
          <Textarea
            value={blueprintDesc || ''}
            onChange={e => setBlueprintDesc(e.target.value || null)}
            className="w-64"
            placeholder="Short description"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-2">
            <Switch id="privacy-toggle" checked={isPublic} onCheckedChange={checked => setIsPublic(checked)} />
            <Label htmlFor="privacy-toggle" className="flex items-center gap-1.5 text-sm">
              {isPublic ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              {isPublic ? 'Public' : 'Private'}
            </Label>
          </div>
          <Button variant="outline" size="sm" onClick={saveLocal}><Copy className="h-4 w-4 mr-2" /> Save Locally</Button>
          <Button variant="default" size="sm" onClick={saveToSupabase}><Save className="h-4 w-4 mr-2" /> Save to Cloud</Button>
          <Button variant="primary" size="sm" className="gap-2" onClick={runBlueprint}><Play className="h-4 w-4" /> Run</Button>
        </div>
      </header>

      <div className="flex-1 w-full overflow-hidden flex">
        <aside className="w-72 border-r flex-shrink-0 h-full overflow-y-auto">
          <NodePalette onDragStart={(event, type) => { event.dataTransfer.setData('application/reactflow', type); event.dataTransfer.effectAllowed = 'move'; }} />
        </aside>

        <main className={cn('flex-1 relative flex h-full min-w-0 overflow-hidden', isPanelOpen ? 'pr-[370px]' : '')}>
          <div ref={reactFlowWrapper} className="flex-1 relative transition-all h-full min-w-0 overflow-hidden">
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesState}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                fitView
                nodeTypes={nodeTypes}
                onDrop={onDrop}
                onDragOver={onDragOver}
                proOptions={{ hideAttribution: true }}
                className="bg-gradient-to-br from-background to-muted/40"
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
              >
                <Background variant="lines" gap={32} color="hsl(var(--border))" />
                <MiniMap
                  nodeColor={(n) => getNodeDef(n.type)?.accentColor || '#888'}
                  pannable
                  zoomable
                  bgColor="hsl(var(--background))"
                  maskColor="hsl(var(--muted))"
                />
                <Controls showInteractive={false} />
              </ReactFlow>
            </ReactFlowProvider>
          </div>

          {selectedNode && (
            <aside className="w-[360px] border-l bg-card h-full flex flex-col absolute right-0 top-0 z-10 shadow-lg">
              <Card className="h-full rounded-none border-0">
                <CardHeader className="border-b">
                  <CardTitle>Node Properties</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col gap-4 py-6">
                  <div><Label>Node Type</Label><Input value={selectedNode.data.type} readOnly className="bg-muted" /></div>
                  <div><Label>Label</Label><Input value={selectedNode.data.label || ''} onChange={(e) => handleNodeDataChange('label', e.target.value)} placeholder="Node label" /></div>
                  <div><Label>Description</Label><Input value={selectedNode.data.description || ''} onChange={(e) => handleNodeDataChange('description', e.target.value)} placeholder="Short description" /></div>

                  {selectedNode.data.type === 'configuration' ? (
                    <div className="space-y-6 pt-2">
                      <div>
                        <div className="flex justify-between items-center">
                          <Label>Temperature</Label>
                          <span className="text-sm font-medium text-muted-foreground">{selectedNode.data.temperature ?? 1}</span>
                        </div>
                        <Slider
                          value={[selectedNode.data.temperature ?? 1]}
                          onValueChange={([value]) => handleNodeDataChange('temperature', value)}
                          max={2}
                          step={0.1}
                        />
                      </div>
                      <div>
                        <Label>Max Tokens</Label>
                        <Input
                          type="number"
                          value={selectedNode.data.max_tokens || ''}
                          onChange={(e) => handleNodeDataChange('max_tokens', parseInt(e.target.value, 10) || undefined)}
                          placeholder="Default (e.g., 1500)"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between items-center">
                          <Label>Top P</Label>
                          <span className="text-sm font-medium text-muted-foreground">{selectedNode.data.top_p ?? 1}</span>
                        </div>
                        <Slider
                          value={[selectedNode.data.top_p ?? 1]}
                          onValueChange={([value]) => handleNodeDataChange('top_p', value)}
                          max={1}
                          step={0.05}
                        />
                      </div>
                    </div>
                  ) : selectedNode.data.type === 'model-selector' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Selected Model</Label>
                        <ModelSelector
                          models={models}
                          selectedModelId={selectedNode.data.modelId || ''}
                          onModelChange={(modelId) => handleNodeDataChange('modelId', modelId)}
                        />
                      </div>
                    </div>
                  ) : selectedNode.data.type === 'demythos' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Task Prompt</Label>
                        <Textarea
                          value={selectedNode.data.prompt || ''}
                          onChange={(e) => handleNodeDataChange('prompt', e.target.value)}
                          placeholder="Use {{input}} or {{lastOutput}} to reference upstream context"
                          rows={5}
                        />
                      </div>
                      <div>
                        <Label>Model</Label>
                        <ModelSelector
                          models={models}
                          selectedModelId={selectedNode.data.modelId || ''}
                          onModelChange={(modelId) => handleNodeDataChange('modelId', modelId)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Max Loops</Label>
                          <Input
                            type="number"
                            min={1}
                            max={50}
                            value={selectedNode.data.maxLoops || 6}
                            onChange={(e) => handleNodeDataChange('maxLoops', parseInt(e.target.value, 10) || 1)}
                          />
                        </div>
                        <div>
                          <Label>Reasoning Mode</Label>
                          <Select
                            value={selectedNode.data.reasoningMode || 'balanced'}
                            onValueChange={(value) => handleNodeDataChange('reasoningMode', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="balanced">Balanced</SelectItem>
                              <SelectItem value="skeptical">Skeptical</SelectItem>
                              <SelectItem value="expansive">Expansive</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Token Budget</Label>
                          <Input
                            type="number"
                            min={0}
                            value={selectedNode.data.tokenBudget || ''}
                            onChange={(e) => handleNodeDataChange('tokenBudget', parseInt(e.target.value, 10) || undefined)}
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label>Credit Budget</Label>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={selectedNode.data.creditBudget || ''}
                            onChange={(e) => handleNodeDataChange('creditBudget', parseFloat(e.target.value) || undefined)}
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label>Time Budget (ms)</Label>
                          <Input
                            type="number"
                            min={0}
                            value={selectedNode.data.timeBudgetMs || ''}
                            onChange={(e) => handleNodeDataChange('timeBudgetMs', parseInt(e.target.value, 10) || undefined)}
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label>Return Mode</Label>
                          <Select
                            value={selectedNode.data.returnMode || 'full-state'}
                            onValueChange={(value) => handleNodeDataChange('returnMode', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="final-answer">Final answer</SelectItem>
                              <SelectItem value="summary-answer">Summary + answer</SelectItem>
                              <SelectItem value="full-state">Full state</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ) : selectedNode.data.type === 'demythos' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Task Prompt</Label>
                        <Textarea
                          value={selectedNode.data.prompt || ''}
                          onChange={(e) => handleNodeDataChange('prompt', e.target.value)}
                          placeholder="Use {{input}} or {{lastOutput}} to reference upstream context"
                          rows={5}
                        />
                      </div>
                      <div>
                        <Label>Model</Label>
                        <ModelSelector
                          models={models}
                          selectedModelId={selectedNode.data.modelId || ''}
                          onModelChange={(modelId) => handleNodeDataChange('modelId', modelId)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Max Loops</Label>
                          <Input
                            type="number"
                            min={1}
                            max={50}
                            value={selectedNode.data.maxLoops || 6}
                            onChange={(e) => handleNodeDataChange('maxLoops', parseInt(e.target.value, 10) || 1)}
                          />
                        </div>
                        <div>
                          <Label>Reasoning Mode</Label>
                          <Select
                            value={selectedNode.data.reasoningMode || 'balanced'}
                            onValueChange={(value) => handleNodeDataChange('reasoningMode', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="balanced">Balanced</SelectItem>
                              <SelectItem value="skeptical">Skeptical</SelectItem>
                              <SelectItem value="expansive">Expansive</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Token Budget</Label>
                          <Input
                            type="number"
                            min={0}
                            value={selectedNode.data.tokenBudget || ''}
                            onChange={(e) => handleNodeDataChange('tokenBudget', parseInt(e.target.value, 10) || undefined)}
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label>Credit Budget</Label>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={selectedNode.data.creditBudget || ''}
                            onChange={(e) => handleNodeDataChange('creditBudget', parseFloat(e.target.value) || undefined)}
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label>Time Budget (ms)</Label>
                          <Input
                            type="number"
                            min={0}
                            value={selectedNode.data.timeBudgetMs || ''}
                            onChange={(e) => handleNodeDataChange('timeBudgetMs', parseInt(e.target.value, 10) || undefined)}
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label>Return Mode</Label>
                          <Select
                            value={selectedNode.data.returnMode || 'full-state'}
                            onValueChange={(value) => handleNodeDataChange('returnMode', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="final-answer">Final answer</SelectItem>
                              <SelectItem value="summary-answer">Summary + answer</SelectItem>
                              <SelectItem value="full-state">Full state</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ) : selectedNode.data.type === 'conditional' ? (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Evaluates the condition against the last output and branches True/False.
                      </p>
                      <div>
                        <Label>Condition</Label>
                        <div className="flex items-center gap-2">
                          <Input value="{{lastOutput}}" disabled className="w-1/3 bg-muted" />
                          <Select
                            value={selectedNode.data.operator || 'contains'}
                            onValueChange={(value) => handleNodeDataChange('operator', value)}
                          >
                            <SelectTrigger className="w-1/3">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="contains">contains</SelectItem>
                              <SelectItem value="not-contains">not contains</SelectItem>
                              <SelectItem value="==">==</SelectItem>
                              <SelectItem value="!=">!=</SelectItem>
                              <SelectItem value=">">&gt;</SelectItem>
                              <SelectItem value="<">&lt;</SelectItem>
                              <SelectItem value=">=">&gt;=</SelectItem>
                              <SelectItem value="<=">&lt;=</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            value={selectedNode.data.value || ''}
                            onChange={(e) => handleNodeDataChange('value', e.target.value)}
                            placeholder="Value"
                            className="w-1/3"
                          />
                        </div>
                      </div>
                    </div>
                  ) : selectedNode.data.type === 'data' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Data Source Type</Label>
                        <Select onValueChange={(value) => handleNodeDataChange('sourceType', value)} defaultValue={selectedNode.data.sourceType || 'static-text'}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select data source" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="static-text">Static Text</SelectItem>
                            <SelectItem value="file-upload">File Upload</SelectItem>
                            <SelectItem value="database">Database Connection</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {(!selectedNode.data.sourceType || selectedNode.data.sourceType === 'static-text') && (
                        <div>
                          <Label>Content</Label>
                          <Textarea value={selectedNode.data.content || ''} onChange={(e) => handleNodeDataChange('content', e.target.value)} placeholder="Static data or prompt" rows={5} />
                        </div>
                      )}

                      {selectedNode.data.sourceType === 'file-upload' && (
                        <div className="space-y-2">
                          <Label>Upload File</Label>
                          <Input
                            type="file"
                            onChange={(e) => e.target.files && handleNodeDataChange('file', { path: e.target.files[0]?.name, name: e.target.files[0]?.name, type: e.target.files[0]?.type })}
                            accept=".txt,.csv,.xlsx"
                          />
                          {selectedNode.data.file && (
                            <div className="text-sm text-muted-foreground">Selected: {selectedNode.data.file.name}</div>
                          )}
                        </div>
                      )}

                      {selectedNode.data.sourceType === 'database' && (
                        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                          <p className="text-sm text-center text-muted-foreground font-semibold">Database connection not implemented in Desktop Studio yet.</p>
                          <div><Label>Database URL</Label><Input placeholder="postgresql://user:password@host:port/db" disabled /></div>
                          <div><Label>SQL Query</Label><Textarea placeholder="SELECT * FROM users;" rows={3} disabled /></div>
                        </div>
                      )}
                    </div>
                  ) : selectedNode.data.type === 'knowledge-base' ? (
                    <div className="space-y-4">
                      <Label>Knowledge Base</Label>
                      {knowledgeBases.length === 0 ? (
                        <Input value="No Knowledge Bases found" readOnly disabled />
                      ) : (
                        <Select
                          value={String(selectedNode.data.content || '')}
                          onValueChange={(value) => handleNodeDataChange('content', value)}
                        >
                          <SelectTrigger><SelectValue placeholder="Select a Knowledge Base" /></SelectTrigger>
                          <SelectContent>
                            {knowledgeBases.map(kb => (<SelectItem key={kb.id} value={kb.id}>{kb.name}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ) : selectedNode.data.type === 'mcp' ? (
                    <div className="space-y-4">
                      <Label>MCP Service</Label>
                      <Select
                        value={String(selectedNode.data.content || '')}
                        onValueChange={(value) => handleNodeDataChange('content', value)}
                      >
                        <SelectTrigger><SelectValue placeholder="Select an MCP service" /></SelectTrigger>
                        <SelectContent>
                          {mcpServices.map(service => (<SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : selectedNode.data.type === 'api-call' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>URL</Label>
                        <Input
                          value={selectedNode.data.url || ''}
                          placeholder="https://api.example.com/resource or full cURL"
                          onChange={(e) => handleNodeDataChange('url', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Method</Label>
                        <Select
                          value={(selectedNode.data.method || 'GET')}
                          onValueChange={(value) => handleNodeDataChange('method', value)}
                        >
                          <SelectTrigger><SelectValue placeholder="HTTP Method" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GET">GET</SelectItem>
                            <SelectItem value="POST">POST</SelectItem>
                            <SelectItem value="PUT">PUT</SelectItem>
                            <SelectItem value="PATCH">PATCH</SelectItem>
                            <SelectItem value="DELETE">DELETE</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Headers (JSON)</Label>
                        <Textarea
                          rows={4}
                          value={(() => { try { return JSON.stringify(selectedNode.data.headers || {}, null, 2); } catch { return '{}'; } })()}
                          onChange={(e) => { try { handleNodeDataChange('headers', JSON.parse(e.target.value || '{}')); } catch { /* ignore */ } }}
                          placeholder='{"Authorization":"Bearer ..."}'
                        />
                      </div>
                      <div>
                        <Label>Body</Label>
                        <Textarea
                          rows={6}
                          value={selectedNode.data.body || ''}
                          onChange={(e) => handleNodeDataChange('body', e.target.value)}
                          placeholder='{"query":"value"} or raw text'
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Label>Content</Label>
                      <Textarea value={selectedNode.data.content || ''} onChange={(e) => handleNodeDataChange('content', e.target.value)} placeholder="Node content or prompt" rows={5} />
                    </div>
                  )}
                </CardContent>
              </Card>
            </aside>
          )}
        </main>
      </div>

      <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Run Blueprint</DialogTitle></DialogHeader>

          {!executionResult ? (
            <>
              <div className="space-y-4 py-4">
                <Label htmlFor="initial-input">Initial User Input</Label>
                <Textarea
                  id="initial-input"
                  placeholder="Enter the initial message or data..."
                  value={runInitialInput}
                  onChange={e => setRunInitialInput(e.target.value)}
                  disabled={isExecuting}
                  rows={4}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRunDialogOpen(false)} disabled={isExecuting}>Cancel</Button>
                <Button onClick={executeFlow} disabled={isExecuting || !runInitialInput.trim()}>
                  {isExecuting ? 'Executing...' : 'Run'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="px-6 space-y-6">
              <div>
                <h3 className={cn('text-lg font-semibold', executionResult.error ? 'text-destructive' : 'text-primary')}>
                  {executionResult.error ? 'Execution Failed' : 'Execution Successful'}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">{executionResult.error ? executionResult.error : 'The blueprint flow has completed.'}</p>
              </div>

              <div>
                <Label className="font-medium mb-2 block">Final Output</Label>
                <div className="relative p-3 rounded-md bg-muted text-sm">
                  <ScrollArea className="h-40 p-2 pr-8 prose prose-sm dark:prose-invert">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {executionResult.finalOutput || 'No output was generated.'}
                    </ReactMarkdown>
                  </ScrollArea>
                  {executionResult.finalOutput && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-6 h-8 w-8"
                      onClick={() => {
                        navigator.clipboard.writeText(executionResult.finalOutput || '');
                      }}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label className="font-medium mb-2 block">Execution History</Label>
                <ScrollArea className="border rounded-md p-2 pb-4 h-40">
                  <div className="space-y-2 text-xs">
                    {executionResult.history.map((entry, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <span className="font-mono text-muted-foreground">[{new Date(entry.timestamp).toLocaleTimeString()}]</span>
                        <p className="whitespace-pre-wrap">{entry.message}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setExecutionResult(null)}>Run Again</Button>
                <Button className="ml-auto" onClick={() => setRunDialogOpen(false)}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}